import { type ReactNode } from 'react';
import { TIER_ROLES_LABEL } from '@ak/shared';

/** Знак Discord одним цветом: в монохроме CARTEL фирменный синий выпадал бы. */
function DiscordLogo({ size = 20 }: { size?: number }): ReactNode {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.317 4.37a19.8 19.8 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.8 19.8 0 0 0 5.996-3.03.08.08 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.06.06 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}

export function LoginPage({
  oauthConfigured: _oauthConfigured,
  devLoginEnabled,
  // Аватар бота на экране входа не нужен: лицо страницы — знак CARTEL.
  botAvatarUrl: _botAvatarUrl,
}: {
  oauthConfigured: boolean;
  devLoginEnabled: boolean;
  botAvatarUrl: string | null;
}): ReactNode {
  return (
    <div className="grid min-h-screen place-items-center p-4">
      <div
        className="w-full max-w-md rounded-2xl p-6 text-center sm:p-9"
        style={{
          background: 'color-mix(in oklab, var(--surface-panel) 82%, transparent)',
          backdropFilter: 'blur(14px)',
          border: '1px solid var(--border-strong)',
          boxShadow: 'var(--shadow-raised)',
        }}
      >
        <span className="brand-logo-hero" role="img" aria-label="Логотип CARTEL" />

        <h1 className="brand-name text-3xl sm:text-4xl">Cartel</h1>
        <div className="mx-auto my-4 h-px w-24" style={{ background: 'var(--border-strong)' }} />
        <p className="mb-7 text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          Панель семьи: наборы на мероприятия, анкеты, заявки, откаты и статистика каптов.
        </p>

        {/*
          Вход — главное действие экрана. Кнопка в цветах темы (белая на
          чёрном), а не фирменно-синяя: так она остаётся частью стиля CARTEL.
        */}
        <a
          href="/auth/login"
          className="flex w-full items-center justify-center gap-2.5 rounded-xl px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] transition-transform hover:-translate-y-0.5"
          style={{ background: 'var(--accent)', color: 'var(--accent-text)', boxShadow: 'var(--shadow-accent)' }}
        >
          <DiscordLogo size={20} />
          Войти через Discord
        </a>

        {/* Локальный тест: сервер отдаёт /auth/dev-login только при
            AUTH_DEV_LOGIN=true на локальном адресе, поэтому в бою кнопки нет. */}
        {devLoginEnabled ? (
          <button
            type="button"
            className="mt-3 w-full rounded-xl px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em]"
            style={{ background: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--border-strong)' }}
            onClick={() => {
              void fetch('/auth/dev-login', { method: 'POST' }).then(() => {
                window.location.reload();
              });
            }}
          >
            Войти без Discord (локальный тест)
          </button>
        ) : null}

        <p className="mt-5 text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          Доступ решают роли на сервере: <span style={{ color: 'var(--text-secondary)' }}>{TIER_ROLES_LABEL}</span> открывают
          статистику каптов, роли модерации — остальные разделы.
        </p>
      </div>
    </div>
  );
}
